import {FormState} from '../../models/form-state.model';
import {Injectable} from '@angular/core';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {AuthActions} from './auth.actions';
import {AuthService} from '../../services/auth.service';
import {catchError, map, tap} from 'rxjs/operators';
import {SystemUser} from '../../models/system-user.model';
import {Navigate} from '@ngxs/router-plugin';
import {EMPTY} from 'rxjs';
import {setFormStatus, setFormValidationErrors} from '../../helpers/form.helper';
import {FormStatus} from '../../enums/file-status.enum';
import {PasswordPolicy} from '../../models/password-policy.model';
import {NotificationService} from '../../services/notification.service';
import {HttpResponse} from '@angular/common/http';
import {RESET_PASSWORD_TOKEN_HEADER} from '../../helpers/request.helper';
import {Partner} from '../../models/partner.model';

interface AuthStateModel {
    loginForm: FormState;
    passwordForgottenForm: FormState;
    passwordResetForm: FormState;
    currentUser: SystemUser;
    passwordPolicy: PasswordPolicy;
}

@State<AuthStateModel>({
    name: AuthState.STATE_NAME,
    defaults: {
        loginForm: new FormState(),
        passwordForgottenForm: new FormState(),
        passwordResetForm: new FormState(),
        currentUser: null,
        passwordPolicy: null
    }
})

@Injectable()
export class AuthState {
    static readonly STATE_NAME = 'auth';

    constructor(
        private authService: AuthService,
        private notificationService: NotificationService,
    ) {
    }

    @Selector()
    static currentUser(state) {
        return state.currentUser;
    }

    @Selector()
    static permissions(state) {
        return state?.currentUser?.availablePermissionAreas || [];
    }

    @Selector()
    static currentUserPartnerIsVirtual(state) {
        return state.currentUser?.partner?.isVirtual;
    }

    @Selector()
    static currentUserAssortmentGroups(state) {
        return state.currentUser?.partner?.assortmentGroups || [];
    }

    @Selector()
    static loginForm(state) {
        return state.loginForm;
    }

    @Selector()
    static passwordForgottenForm(state) {
        return state.passwordForgottenForm;
    }

    @Selector()
    static passwordResetForm(state) {
        return state.passwordResetForm;
    }

    @Selector()
    static loginFormErrors(state) {
        return state.loginForm.validationErrors;
    }

    @Selector()
    static passwordResetFormErrors(state) {
        return state.passwordResetForm.validationErrors;
    }

    @Selector()
    static passwordForgottenFormErrors(state) {
        return state.passwordForgottenForm.validationErrors;
    }

    @Selector()
    static passwordPolicy(state) {
        return state.passwordPolicy;
    }

    @Action(AuthActions.AuthSuccess)
    saveCurrentUser(ctx: StateContext<AuthStateModel>, {user}: AuthActions.AuthSuccess) {
        const currentUser = new SystemUser({
            ...user,
            partner: new Partner(user.partner)
        });
        ctx.patchState({
            currentUser
        });
    }

    @Action(AuthActions.AuthFailure)
    authFailure(ctx: StateContext<AuthStateModel>) {
        ctx.dispatch(new Navigate(['/login']));
    }

    @Action(AuthActions.LoginUser)
    authUser(ctx: StateContext<AuthStateModel>, action: AuthActions.LoginUser) {
        const {login, password} = action;
        return this.authService.login(login, password).pipe(
            map((response: HttpResponse<SystemUser>) => {
                const token = response.headers.get(RESET_PASSWORD_TOKEN_HEADER);
                if (token) {
                    ctx.dispatch(new Navigate(['/login/reset-old-password', token]));
                } else {
                    const user = new SystemUser(response?.body);
                    if (user.username) {
                        ctx.dispatch(new AuthActions.AuthSuccess(user));
                        ctx.dispatch(new Navigate(['/start']));
                        return true;
                    }
                }
                return false;
            }),
            catchError(() => {
                setFormStatus(ctx, FormStatus.INVALID, AuthState.STATE_NAME, 'loginForm');
                return EMPTY;
            })
        );
    }

    @Action(AuthActions.Logout)
    logout(ctx: StateContext<AuthStateModel>) {
        ctx.patchState({
            currentUser: null
        });
        ctx.dispatch(new Navigate(['/login']));
    }

    @Action(AuthActions.LogoutRequest)
    logoutRequest(ctx: StateContext<AuthStateModel>) {
        return this.authService.logout().pipe(
            tap(() => ctx.dispatch(new AuthActions.Logout())),
            catchError(() => {
                ctx.dispatch(new AuthActions.Logout());
                return EMPTY;
            })
        );
    }

    @Action(AuthActions.PasswordForgotten)
    passwordForgot(ctx: StateContext<AuthStateModel>, {username}: AuthActions.PasswordForgotten) {
        return this.authService.passwordForgotten(username).pipe(
            tap(() => {
                setFormStatus(ctx, FormStatus.SUCCESS, AuthState.STATE_NAME, 'passwordForgottenForm');
            }),
            catchError((httpError) => {
                const {error = []} = httpError;
                setFormValidationErrors(ctx, error, AuthState.STATE_NAME, 'passwordForgottenForm');
                return EMPTY;
            })
        );
    }

    @Action(AuthActions.PasswordReset)
    passwordReset(ctx: StateContext<AuthStateModel>, {token, formData}: AuthActions.PasswordReset) {
        return this.authService.passwordReset(token, formData).pipe(
            tap(() => {
                ctx.dispatch(new Navigate(['/login']));
            }),
            catchError((httpError) => {
                const {error = []} = httpError;
                setFormValidationErrors(ctx, error, AuthState.STATE_NAME, 'passwordResetForm');
                return EMPTY;
            })
        );
    }

    @Action(AuthActions.GetPasswordPolicy)
    getPasswordPolicy(ctx: StateContext<AuthStateModel>, {token}: AuthActions.GetPasswordPolicy) {
        return this.authService.getPasswordPolicy(token).pipe(
            tap((policy) => {
                ctx.patchState({
                    passwordPolicy: policy
                });
            }),
            catchError((httpError) => {
                if (httpError?.status === 404) {
                    this.notificationService.error('ERRORS.PASSWORD_RESET_REQUEST_RESPONSE_404');
                    ctx.dispatch(new Navigate(['/login']));
                }
                return EMPTY;
            })
        );
    }

}
